/**
 * Notes Tool Definitions
 * Tool definitions for agent integration
 */

import { ToolCategory } from 'tool_sdk'
import { forwardCall, forwardCallByName } from 'workers-common'

export const toolDefinitions = {
  saveNote: {
    name: 'saveNote',
    description:
      'Save information that user wants to remember (notes, facts, preferences, etc.)',
    parameters: {
      type: 'object',
      properties: {
        content: {
          type: 'string',
          description: 'Note content (supports markdown)',
        },
        title: {
          type: 'string',
          description: 'Optional title (auto-generated if not provided)',
        },
        tags: {
          type: 'array',
          items: { type: 'string' },
          description: 'Optional tags for categorization',
        },
      },
      required: ['content'],
    },
  },

  searchNotes: {
    name: 'searchNotes',
    description: 'Search user notes by semantic meaning',
    parameters: {
      type: 'object',
      properties: {
        query: {
          type: 'string',
          description: 'Search query',
        },
        limit: {
          type: 'number',
          description: 'Max results (default: 5)',
        },
      },
      required: ['query'],
    },
  },

  listNotes: {
    name: 'listNotes',
    description: 'List all notes for current user',
    parameters: {
      type: 'object',
      properties: {
        limit: {
          type: 'number',
          description: 'Max results (default: 10)',
        },
        offset: {
          type: 'number',
          description: 'Skip N notes (default: 0)',
        },
      },
    },
  },

  updateNoteByQuery: {
    name: 'updateNoteByQuery',
    description: 'Update a note by searching for it first',
    parameters: {
      type: 'object',
      properties: {
        query: {
          type: 'string',
          description: 'Search query to find the note',
        },
        newContent: {
          type: 'string',
          description: 'New content for the note',
        },
      },
      required: ['query', 'newContent'],
    },
  },

  updateNote: {
    name: 'updateNote',
    description: 'Update a note by its ID',
    parameters: {
      type: 'object',
      properties: {
        noteId: {
          type: 'string',
          description: 'Note ID',
        },
        content: {
          type: 'string',
          description: 'New content',
        },
        title: {
          type: 'string',
          description: 'New title',
        },
        tags: {
          type: 'array',
          items: { type: 'string' },
          description: 'New tags',
        },
      },
      required: ['noteId'],
    },
  },

  deleteNote: {
    name: 'deleteNote',
    description: 'Delete a note by its ID',
    parameters: {
      type: 'object',
      properties: {
        noteId: {
          type: 'string',
          description: 'Note ID',
        },
      },
      required: ['noteId'],
    },
  },
}

/**
 * Create notes category with all tools
 * @param {Object} config
 * @param {Function} config.getWorkerClient - Function to get WorkerClient instance
 * @param {string} config.notesPublicKey - Public key of the notes worker
 * @returns {ToolCategory} Notes category
 */
export function createNotesCategory({ getWorkerClient, notesPublicKey, getTarget }) {
  const callNotes = async (ctx, method, args) => {
    const workerClient = getWorkerClient()
    if (getTarget) {
      const target = await getTarget()
      return forwardCallByName(ctx, workerClient, target, `notes.${method}`, [args])
    }
    return forwardCall(ctx, workerClient, notesPublicKey, `notes.${method}`, [args])
  }

  const fromCall = (method) => (ctx, args) => callNotes(ctx, method, args)

  return new ToolCategory({
    id: 'notes',
    description: `Save, search, and manage user notes`,
    tools: [
      { definition: toolDefinitions.saveNote, implementation: fromCall('save') },
      { definition: toolDefinitions.searchNotes, implementation: fromCall('search') },
      { definition: toolDefinitions.listNotes, implementation: fromCall('list') },
      { definition: toolDefinitions.updateNote, implementation: fromCall('update') },
      { definition: toolDefinitions.updateNoteByQuery, implementation: fromCall('updateByQuery') },
      { definition: toolDefinitions.deleteNote, implementation: fromCall('delete') },
    ],
    subCategories: [],
  })
}
